import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/config';
import { useAuth } from '../../context/AuthContext';
import { ScrollText, User, Clock, Info, Search, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FilterDropdown from '../dashboard/promotions/components/FilterDropdown';

const Activities = () => {
    const { userData, effectiveTenantId, hasPermission, permissionsLoading } = useAuth();
    const { t, i18n } = useTranslation();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        const fetchActivities = async () => {
            let query = supabase
                .from('activities')
                .select('*')
                .neq('action', 'LOG_INTERACTION') // Exclude customer interactions here
                .order('timestamp', { ascending: false })
                .limit(100);

            if (!effectiveTenantId) {
                if (!userData?.isSuperAdmin) {
                    setLoading(false);
                    return;
                }
                // Global view for super admin - no tenant filter
            } else {
                query = query.eq('tenant_id', effectiveTenantId);
            }

            const { data, error } = await query;
            if (error) {
                console.error("Activity Fetch Error:", error);
                setLoading(false);
                return;
            }

            const mapped = (data || []).map(row => ({
                id: row.id,
                action: row.action,
                details: row.details,
                userEmail: row.user_email,
                userId: row.user_id,
                tenantId: row.tenant_id,
                timestamp: row.timestamp,
            }));
            setActivities(mapped);
            setLoading(false);
        };

        fetchActivities();

        // Subscribe to realtime changes
        const channelFilter = effectiveTenantId
            ? `tenant_id=eq.${effectiveTenantId}`
            : undefined;

        const channel = supabase
            .channel('activities-realtime')
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'activities',
                ...(channelFilter ? { filter: channelFilter } : {}),
            }, () => {
                fetchActivities();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    const getActionColor = (action) => {
        const colors = {
            'CREATE_CUSTOMER': '#10b981',
            'UPDATE_CUSTOMER': '#3b82f6',
            'DELETE_CUSTOMER': '#ef4444',
            'LOG_INTERACTION': '#8b5cf6',
            'CREATE_DISCOUNT': '#10b981',
            'UPDATE_DISCOUNT': '#3b82f6',
            'DELETE_DISCOUNT': '#ef4444',
            'CREATE_REWARD': '#10b981',
            'UPDATE_REWARD': '#3b82f6',
            'DELETE_REWARD': '#ef4444',
            'SEND_NOTIFICATION': '#f59e0b',
            'UPDATE_NOTIFICATION': '#3b82f6',
            'CREATE_ROLE': '#10b981',
            'UPDATE_ROLE': '#3b82f6',
            'DELETE_ROLE': '#ef4444',
            'INVITE_USER': '#f59e0b',
            'UPDATE_SETTINGS': '#6366f1',
            'UPDATE_THEME': '#ec4899',
            'LOGIN': '#f59e0b'
        };
        return colors[action] || 'var(--text-muted)';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return new Intl.DateTimeFormat(i18n.language === 'tr' ? 'tr-TR' : 'en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            return 'Date Error';
        }
    };

    const filterOptions = [
        { id: 'all', label: t('common.all', 'Tümü') },
        { id: 'campaigns', label: t('nav.campaigns', 'Kampanyalar') },
        { id: 'settings', label: t('roles.title', 'Ayarlar & Roller') },
        { id: 'notifications', label: t('nav.notifications', 'Bildirimler') },
        { id: 'auth', label: t('roles.rolesTab', 'Giriş & Güvenlik') }
    ];

    const filteredActivities = (activities || []).filter(a => {
        const matchesSearch = 
            a.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.action?.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (statusFilter === 'all') return matchesSearch;

        const action = a.action || '';
        if (statusFilter === 'campaigns') {
            return matchesSearch && (action.includes('DISCOUNT') || action.includes('REWARD') || action.includes('COUPON'));
        }
        if (statusFilter === 'settings') {
            return matchesSearch && (action.includes('SETTINGS') || action.includes('THEME') || action.includes('ROLE'));
        }
        if (statusFilter === 'notifications') {
            return matchesSearch && action.includes('NOTIFICATION');
        }
        if (statusFilter === 'auth') {
            return matchesSearch && action.includes('LOGIN');
        }

        return matchesSearch;
    });

    const parseDetail = (detail) => {
        if (!detail) return '';
        // Try to replace common English patterns with localized ones
        let localizedDetail = detail;

        // Pattern matching for "Action Type: Item Name"
        const patterns = [
            { en: 'Created discount:', tr: 'İndirim oluşturuldu:', key: 'discount', verb: 'created' },
            { en: 'Updated discount:', tr: 'İndirim güncellendi:', key: 'discount', verb: 'updated' },
            { en: 'Deleted discount:', tr: 'İndirim silindi:', key: 'discount', verb: 'deleted' },
            { en: 'Created reward:', tr: 'Ödül oluşturuldu:', key: 'reward', verb: 'created' },
            { en: 'Updated reward:', tr: 'Ödül güncellendi:', key: 'reward', verb: 'updated' },
            { en: 'Deleted reward:', tr: 'Ödül silindi:', key: 'reward', verb: 'deleted' },
            { en: 'Sent notification:', tr: 'Bildirim gönderildi:', key: 'notification', verb: 'sent' },
            { en: 'Updated notification:', tr: 'Bildirim güncellendi:', key: 'notification', verb: 'updated' },
            { en: 'Deleted notification:', tr: 'Bildirim silindi:', key: 'notification', verb: 'deleted' },
            { en: 'Created role:', tr: 'Rol oluşturuldu:', key: 'role', verb: 'created' },
            { en: 'Updated role:', tr: 'Rol güncellendi:', key: 'role', verb: 'updated' },
            { en: 'Deleted role:', tr: 'Rol silindi:', key: 'role', verb: 'deleted' },
            { en: 'Invited user:', tr: 'Kullanıcı davet edildi:', key: 'customer', verb: 'invited' },
            { en: 'Updated customer:', tr: 'Müşteri güncellendi:', key: 'customer', verb: 'updated' },
            { en: 'Created customer:', tr: 'Müşteri oluşturuldu:', key: 'customer', verb: 'created' },
        ];

        for (const p of patterns) {
            if (detail.includes(p.en) || detail.includes(p.tr)) {
                const parts = detail.split(':');
                const itemName = parts.length > 1 ? parts.slice(1).join(':') : '';

                if (i18n.language === 'tr') {
                    return `${t(`activities.details.${p.key}`)} ${p.verb === 'sent' || p.verb === 'invited' ? 'edildi' : ''} ${p.verb === 'created' ? 'oluşturuldu' : p.verb === 'updated' ? 'güncellendi' : 'silindi'}: ${itemName}`;
                } else {
                    return `${t(`activities.details.${p.verb}`)} ${t(`activities.details.${p.key}`)}: ${itemName}`;
                }
            }
        }

        // Special case for theme
        if (detail.includes('Updated company logo')) {
            return t('activities.details.updated') + ' ' + t('activities.details.theme');
        }

        return detail;
    };

    if (permissionsLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%' }}></div>
            </div>
        );
    }

    if (!userData?.isSuperAdmin && !hasPermission('activities', 'view')) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <ScrollText size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.5 }} />
                <h3>{t('common.accessDenied')}</h3>
                <p style={{ color: 'var(--text-muted)' }}>{t('common.noPermission')}</p>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h1 style={{ fontWeight: 800 }}>{t('activities.title')}</h1>
                    <p style={{ color: 'var(--text-muted)' }}>{t('activities.subtitle')}</p>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
                <div className="search-container" style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={t('common.search')}
                        style={{ paddingLeft: '40px', width: '100%', height: '46px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                    />
                </div>
                <FilterDropdown
                    options={filterOptions}
                    activeFilter={statusFilter}
                    setActiveFilter={setStatusFilter}
                    t={t}
                />
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                            <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>{t('activities.date')}</th>
                            {!effectiveTenantId && userData?.isSuperAdmin && (
                                <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>Tenant ID</th>
                            )}
                            <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>{t('activities.user')}</th>
                            <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>{t('activities.action')}</th>
                            <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>{t('activities.detail')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4" style={{ padding: '2rem', textAlign: 'center' }}>{t('common.loading')}</td></tr>
                        ) : filteredActivities.length === 0 ? (
                            <tr><td colSpan="4" style={{ padding: '2rem', textAlign: 'center' }}>{t('common.noResults', 'Sonuç bulunamadı.')}</td></tr>
                        ) : filteredActivities.map((activity) => (
                            <tr key={activity.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }}>
                                <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)' }}>
                                        <Clock size={14} />
                                        {formatDate(activity.timestamp)}
                                    </div>
                                </td>
                                {!effectiveTenantId && userData?.isSuperAdmin && (
                                    <td style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                        {activity.tenantId}
                                    </td>
                                )}
                                <td style={{ padding: '1rem 1.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '50%',
                                            background: 'var(--bg-main)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.75rem',
                                            fontWeight: 600
                                        }}>
                                            {activity.userEmail?.[0]?.toUpperCase() || '?'}
                                        </div>
                                        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{activity.userEmail || 'Unknown'}</span>
                                    </div>
                                </td>
                                <td style={{ padding: '1rem 1.5rem' }}>
                                    <span style={{
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        background: `${getActionColor(activity.action)}15`,
                                        color: getActionColor(activity.action),
                                        textTransform: 'uppercase'
                                    }}>
                                        {t(`activities.actions.${activity.action}`, { defaultValue: activity.action?.replace(/_/g, ' ') })}
                                    </span>
                                </td>
                                <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem' }}>
                                    {parseDetail(activity.details)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Activities;
